"use client";

import {useState, useEffect} from "react";
import Link from "next/link";
import {usePathname, useParams} from "next/navigation";
import {Params} from "next/dist/server/request/params";
import type {Breadcrumb} from "@/features/breadcrumbs";
import {breadcrumb as killfeedBreadcrumb} from "@/app/dusk-stats/[wipeId]/killfeed/layout";
import {breadcrumb as leaderboardBreadcrumb} from "@/app/dusk-stats/[wipeId]/leaderboard/layout";
import {breadcrumb as settingsBreadcrumb} from "@/app/dusk-stats/[wipeId]/settings/layout";
import {breadcrumb as weaponsBreadcrumb} from "@/app/dusk-stats/[wipeId]/weapons/layout";
import {renderComponent} from "@/utils/jsx";
import cn from "@/utils/cn";
import {resolvePagePath} from "@/utils/path";
import {baseAppDirname} from "@/config/constants";

const breadcrumbs =
[
    killfeedBreadcrumb,
    leaderboardBreadcrumb,
    weaponsBreadcrumb,
    settingsBreadcrumb,
];

type Props =
{
    params: Params;
    variant?: "bottom" | "header";
};

export default function Navigation({params, variant = "bottom"}: Props)
{
    const [links, setLinks] = useState(() => generateLinksFromBreadcrumbs(breadcrumbs, params));
    const pathname = usePathname();
    params = useParams() || params;

    useEffect(() => 
    {
        const links = generateLinksFromBreadcrumbs(breadcrumbs, params);
        setLinks(links);
    },
    [pathname]);

    return (
        <nav className={cn(
            "z-200 bg-neutral-950 flex items-center max-w-full overflow-x-auto h-10 border-b grow justify-end-safe",
            variant === "bottom" && "fixed bottom-0 left-0 border-t",
            // variant === "header" && "h-full"
        )}>
            {links.map(({dirname, href, title, icon}) =>
                <Link key={dirname} href={href || "#"} className={cn(
                    "bg-neutral-950 h-full text-neutral-300 flex items-center gap-1.5 border-y-3 border-y-transparent",
                    "hover:border-b-3 hover:border-b-blue-300 hover:text-neutral-100 px-2.5",
                    href === pathname && "border-b-3 border-b-blue-500", 
                )}>
                    {icon}
                    {title}
                </Link>
            )}
        </nav>
    );
}

function generateLinksFromBreadcrumbs(breadcrumbs: Breadcrumb[], params?: Params)
{
    return breadcrumbs.map((breadcrumb) =>
    {
        const href = breadcrumb.href ?? resolvePagePath(breadcrumb.dirname, params, baseAppDirname);
        const icon = renderComponent(breadcrumb.icon, {className: "size-[1em]"});

        return {href, icon, dirname: breadcrumb.dirname, title: breadcrumb.title};
    });
}
